import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { type OfferRow, type TrafficRow, api } from "../lib/api";

type OffersResp = { items: OfferRow[]; scope: "own" | "public" };
type TrafficResp = { items: TrafficRow[]; scope: "own" | "public" };

export function Directory() {
	const offersQ = useQuery<OffersResp>({
		queryKey: ["offers"],
		queryFn: () => api.get("/offers"),
	});
	const trafficQ = useQuery<TrafficResp>({
		queryKey: ["traffic"],
		queryFn: () => api.get("/traffic"),
	});

	if (offersQ.isLoading || trafficQ.isLoading) return <div className="p-6">Loading…</div>;

	const myOffers = offersQ.data?.scope === "own" ? offersQ.data.items : [];
	const myTraffic = trafficQ.data?.scope === "own" ? trafficQ.data.items : [];
	const publicOffers = offersQ.data?.scope === "public" ? offersQ.data.items : [];
	const publicTraffic = trafficQ.data?.scope === "public" ? trafficQ.data.items : [];

	return (
		<div className="p-6 space-y-8">
			<h1 className="text-xl font-semibold">Directory</h1>
			<section>
				<h2 className="mb-2 text-lg font-medium">
					{publicOffers.length > 0 ? "Active offers" : "Active affiliate traffic"}
				</h2>
				{publicOffers.length > 0 && (
					<ul className="space-y-2">
						{publicOffers.slice(0, 50).map((o) => (
							<PublicOfferCard key={o.id} offer={o} />
						))}
					</ul>
				)}
				{publicTraffic.length > 0 && (
					<ul className="space-y-2">
						{publicTraffic.slice(0, 50).map((t) => (
							<li key={t.id} className="rounded border border-neutral-200 p-3">
								<div className="flex items-baseline justify-between">
									<div className="font-medium">{t.displayName}</div>
									<div className="text-xs text-neutral-500">
										{t.vertical} · {safeArr(t.geos).join(", ")} · {safeArr(t.sources).join("/")}
									</div>
								</div>
								<div className="text-sm text-neutral-600">
									{t.monthlyVolume ?? "?"} conv/mo · fraud {t.fraudScore ?? "?"}
								</div>
							</li>
						))}
					</ul>
				)}
				{publicOffers.length === 0 && publicTraffic.length === 0 && (
					<div className="text-neutral-500">Empty.</div>
				)}
			</section>
			<section>
				<h2 className="mb-2 text-lg font-medium">Your active inventory</h2>
				<div className="text-sm text-neutral-600">
					{myOffers.length} offer(s), {myTraffic.length} traffic profile(s).
				</div>
			</section>
		</div>
	);
}

function PublicOfferCard(props: { offer: OfferRow }) {
	const qc = useQueryClient();
	const nav = useNavigate();
	const o = props.offer;

	const startMut = useMutation({
		mutationFn: () => api.post<{ id: string; state: string }>("/negotiations", { offerId: o.id }),
		onSuccess: (data) => {
			qc.invalidateQueries({ queryKey: ["negotiations"] });
			nav("/negotiations");
		},
	});

	return (
		<li className="rounded border border-neutral-200 p-3">
			<div className="flex items-baseline justify-between">
				<div className="font-medium">{o.title}</div>
				<div className="text-xs text-neutral-500">
					{o.vertical} · {safeArr(o.geos).join(", ")}
				</div>
			</div>
			<div className="mt-1 flex items-center justify-between">
				<div className="text-sm text-neutral-600">
					{o.payoutType} {o.payoutAmount ?? `${o.payoutPercent}%`} {o.payoutCurrency}
				</div>
				<button
					type="button"
					onClick={() => startMut.mutate()}
					disabled={startMut.isPending}
					className="rounded bg-blue-600 px-3 py-1 text-xs text-white hover:bg-blue-700 disabled:opacity-50"
				>
					{startMut.isPending ? "Starting…" : "Start negotiation"}
				</button>
			</div>
			{startMut.error && (
				<div className="mt-1 text-xs text-red-700">{(startMut.error as Error).message}</div>
			)}
		</li>
	);
}

function safeArr(s: string | null): string[] {
	if (!s) return [];
	try {
		const v = JSON.parse(s);
		return Array.isArray(v) ? (v as string[]) : [];
	} catch {
		return [];
	}
}
